import { Checkbox, Select, Table, Tag } from 'antd';
import type { TableProps } from 'antd/es/table';
import type React from 'react';
import { FormattedNumber } from 'react-intl';
import { compareByAlphabet } from 'src/helpers/sorting';
import { useGetPlatformsData } from 'src/hooks/data/platforms';
import type { UpdateBrandsPayload } from 'src/types/platforms';
import { BrandsTable } from './brands-table/brands-table';

const compareByStatus = (status: string) => (status === 'active' ? 0 : status === 'indeterminate' ? 1 : 2);

const compareByPriority = (priority: string) =>
  priority === 'high' ? 3 : priority === 'normal' ? 2 : priority === 'low' ? 1 : 0;

interface PlatformsTableProps {
  searchQuery: string;
  showOnlyNew: boolean;
  editedBrands: UpdateBrandsPayload;
  onBrandsChange: (payload: UpdateBrandsPayload) => void;
}

export const PlatformsTable: React.FC<PlatformsTableProps> = ({
  searchQuery,
  showOnlyNew,
  editedBrands,
  onBrandsChange,
}) => {
  const { data, isInProgress } = useGetPlatformsData({ withInactive: true });
  const searchQueryValue = searchQuery.trim().toLowerCase();

  const dataSource = data
    ?.map((platform) => {
      let brands = platform.brands.map((brand) => {
        const foundBrand = editedBrands.find(({ id }) => id === brand.id);

        if (foundBrand) {
          return {
            ...brand,
            ...foundBrand,
          };
        }

        return brand;
      });
      const hiddenCount = brands.filter((brand) => brand.cmsStatus === 'inactive').length;
      const allHidden = hiddenCount === brands.length;
      const isIndeterminate = hiddenCount > 0 && !allHidden;
      const allPrioritiesSame = brands.every((brand) => brand.priority === brands[0].priority);
      const activeUsers30D = brands.reduce((prev, cur) => prev + cur.activeUsers30D, 0);
      const brandsAmount = brands.length;

      if (showOnlyNew) {
        brands = brands.filter((brand) => brand.isNew);
      }

      let hasBrandMatch = false;

      if (searchQueryValue) {
        const platformMatches = platform.zingPlatformName.toLowerCase().includes(searchQueryValue);
        hasBrandMatch = brands.some((brand) => brand.externalBrandName.toLowerCase().includes(searchQueryValue));

        if (!platformMatches) {
          brands = brands.filter((brand) => brand.externalBrandName.toLowerCase().includes(searchQueryValue));
        }
      }

      if (brands.length === 0) {
        return null;
      }

      return {
        ...platform,
        brands,
        boStatus: brands.some((brand) => brand.boStatus === 'active') ? 'active' : 'inactive',
        cmsStatus: allHidden ? 'inactive' : isIndeterminate ? 'indeterminate' : 'active',
        priority: allPrioritiesSame ? brands[0].priority : 'mixed',
        activeUsers30D,
        brandsAmount,
        hasBrandMatch,
      };
    })
    .filter((row) => row !== null);

  const columns: TableProps<NonNullable<typeof dataSource>[0]>['columns'] = [
    {
      title: 'Platform',
      dataIndex: 'zingPlatformName',
      key: 'zingPlatformName',
      sorter: (a, b) => compareByAlphabet(a.zingPlatformName, b.zingPlatformName),
      filters: data
        ?.map(({ zingPlatformName }) => ({ text: zingPlatformName, value: zingPlatformName }))
        .sort((a, b) => compareByAlphabet(a.value, b.value)),
      filterSearch: true,
      onFilter: (value, record) => record.zingPlatformName === value,
      render: (value, record) => {
        const isNew = record.brands.some((brand) => brand.isNew);

        return (
          <div className="flex items-center gap-x-4">
            <span className="truncate">{value}</span>
            {isNew && <div className="size-1_5 rounded-full bg-primary" />}
          </div>
        );
      },
      ellipsis: true,
    },
    {
      title: 'Status',
      key: 'boStatus',
      dataIndex: 'boStatus',
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive in BO', value: 'inactive' },
      ],
      onFilter: (value, record) => record.boStatus === value,
      render: (value) =>
        value === 'active' ? (
          <span className="flex items-center gap-x-6">
            <span className="inline-block size-12 rounded-full bg-green-6" />
            <span className="text-[12px]/[20px]">Active</span>
          </span>
        ) : (
          <span className="flex items-center gap-x-6">
            <span className="inline-block size-12 rounded-full bg-red-6" />
            <span className="text-[12px]/[20px] text-red-6">Inactive in BO</span>
          </span>
        ),
      width: 168,
    },
    {
      title: (
        <div className="text-left">
          <div className="text-[12px]/[16px]">Hide from</div>
          <div className="text-[12px]/[16px]">CMS</div>
        </div>
      ),
      key: 'cmsStatus',
      dataIndex: 'cmsStatus',
      sorter: (a, b) => compareByStatus(a.cmsStatus) - compareByStatus(b.cmsStatus),
      filters: [
        { text: 'Visible', value: 'active' },
        { text: 'Partially hidden', value: 'indeterminate' },
        { text: 'Hidden', value: 'inactive' },
      ],
      onFilter: (value, record) => record.cmsStatus === value,
      render: (value, record) => (
        <Checkbox
          checked={value === 'inactive'}
          indeterminate={value === 'indeterminate'}
          onChange={(e) =>
            onBrandsChange(
              record.brands.map((brand) => ({ id: brand.id, cmsStatus: e.target.checked ? 'inactive' : 'active' })),
            )
          }
        />
      ),
      align: 'center',
      className: 'py-0 px-8',
      width: 108,
    },
    {
      title: '# of Brands',
      key: 'brandsAmount',
      dataIndex: 'brandsAmount',
      sorter: (a, b) => a.brandsAmount - b.brandsAmount,
      align: 'right',
      className: 'px-8',
      width: 116,
    },
    {
      title: 'Priority',
      key: 'priority',
      dataIndex: 'priority',
      sorter: (a, b) => compareByPriority(a.priority) - compareByPriority(b.priority),
      sortDirections: ['descend', 'ascend'],
      filters: [
        { text: 'High', value: 'high' },
        { text: 'Normal', value: 'normal' },
        { text: 'Low', value: 'low' },
        { text: 'Mixed', value: 'mixed' },
      ],
      onFilter: (value, record) => record.priority === value,
      render: (value, record) => (
        <Select
          value={value}
          labelRender={
            value === 'mixed'
              ? () => (
                  <Tag color="warning" className="font-normal">
                    Mixed
                  </Tag>
                )
              : undefined
          }
          options={[
            {
              label: (
                <Tag color="success" className="font-normal">
                  High
                </Tag>
              ),
              value: 'high',
            },
            { label: <Tag className="font-normal">Normal</Tag>, value: 'normal' },
            {
              label: (
                <Tag color="error" className="font-normal">
                  Low
                </Tag>
              ),
              value: 'low',
            },
          ]}
          onChange={(next) => onBrandsChange(record.brands.map((brand) => ({ id: brand.id, priority: next })))}
          size="small"
          classNames={{
            root: 'border-0 shadow-none pl-0 bg-transparent',
          }}
          className="min-w-[102px]"
        />
      ),
      className: 'py-0',
      width: 176,
    },
    {
      title: (
        <div>
          <div className="text-[12px]/[16px]">Players</div>
          <div className="text-[12px]/[16px] font-normal">Last 30 days</div>
        </div>
      ),
      key: 'activeUsers30D',
      dataIndex: 'activeUsers30D',
      sorter: (a, b) => a.activeUsers30D - b.activeUsers30D,
      render: (value) => <FormattedNumber value={value} />,
      align: 'right',
      className: 'py-0 pr-8',
      width: 160,
    },
  ];

  const defaultExpandedRowKeys = dataSource?.filter((row) => row.hasBrandMatch).map((row) => row.zingPlatformName);

  return (
    <Table
      key={searchQueryValue}
      rowKey="zingPlatformName"
      dataSource={dataSource}
      columns={columns}
      loading={isInProgress}
      pagination={false}
      showSorterTooltip={false}
      sticky
      expandable={{
        defaultExpandedRowKeys,
        expandedRowRender: (record) => (
          <BrandsTable data={record.brands} editedBrands={editedBrands} onBrandsChange={onBrandsChange} />
        ),
      }}
    />
  );
};
